import type { ZodSimplify } from '@mntn-dev/utility-types'
import { z } from 'zod'
import type { PackageSource } from '../package.models.ts'
import {
  BillingMethodSchema,
  type NoneableBillingMethod,
} from './billing-method.ts'
import { MntnCreditProgramIdSchema } from './mntn-credit-program-id.ts'

export const CreditProgramKindSchema = BillingMethodSchema.exclude([
  'invoice',
  'stripe',
])

export const MoneyPaymentMethodSchema = BillingMethodSchema.exclude(
  CreditProgramKindSchema.options
)
export type MoneyPaymentMethod = ZodSimplify<typeof MoneyPaymentMethodSchema>

export const isMoneyPaymentMethod = (
  value: NoneableBillingMethod
): value is MoneyPaymentMethod => {
  return MoneyPaymentMethodSchema.safeParse(value).success
}

export type CreditProgramKind = ZodSimplify<typeof CreditProgramKindSchema> // "mntn_credits" | "meta" | "tiktok" | "linkedin"

export const AgencyApprovedProgramKindSchema = BillingMethodSchema.exclude([
  'invoice',
  'stripe',
  'linkedin',
])

export type AgencyApprovedProgramKind = ZodSimplify<
  typeof AgencyApprovedProgramKindSchema
> // "mntn_credits" | "meta" | "tiktok"

export const isCreditProgramKind = (
  value: NoneableBillingMethod
): value is CreditProgramKind => {
  return CreditProgramKindSchema.safeParse(value).success
}

export function isCreditProgramId(value: string): value is CreditProgramKind {
  return CreditProgramKindSchema.safeParse(value).success
}

export const isMNTNCreativeProgram = (
  value?: NoneableBillingMethod | CreditProgramKind
): boolean => {
  return value === 'mntn_credits'
}

export const CreditProgramRequiresMembership: Record<
  CreditProgramKind,
  boolean
> = {
  mntn_credits: true,
  meta: false,
  tiktok: false,
  linkedin: false,
}

export const OtherCreditProgramMembershipIdSchema = z
  .string()
  .min(1)
  .max(128)
  .brand<'other-credit-program-membership'>()

export const CreditProgramMembershipIdSchema = z.union([
  MntnCreditProgramIdSchema,
  OtherCreditProgramMembershipIdSchema,
])

export type CreditProgramMembershipId = ZodSimplify<
  typeof CreditProgramMembershipIdSchema
>

export const CreditProgramMembershipId = (value: string) => {
  return CreditProgramMembershipIdSchema.parse(value)
}

export const CreditProgramPackageSources: Record<
  CreditProgramKind,
  PackageSource
> = {
  mntn_credits: 'ctv',
  meta: 'meta',
  tiktok: 'tiktok',
  linkedin: 'linkedin',
}
